import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  MapPin,
  Sparkles,
  BedDouble,
  Landmark,
  ShieldCheck,
  Leaf,
  FileText,
  FolderSearch,
  ClipboardList,
  SearchCheck,
  Gavel,
  CheckCircle2,
  ArrowRight,
  Download,
  ClipboardCheck,
  Lock,
  CreditCard,
  Tag,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const BADGE_URLS = {
  gold: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/3qf4e4f0_Black%20And%20White%20Simple%20Icon%20Logo.png",
  silver: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/80ndwcjh_Black%20And%20White%20Simple%20Icon%20Logo%20%281%29.png",
  bronze: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/o5vrg8qt_Black%20And%20White%20Simple%20Icon%20Logo%20%282%29.png",
};

const pillars = [
  {
    icon: MapPin,
    title: "Destination",
    description:
      "Accessibility, digital presence, destination promotion, visitor information, and contribution to the destination experience.",
  },
  {
    icon: Sparkles,
    title: "Hospitality",
    description:
      "Guest experience, customer service, food & beverage, gastronomy, staff excellence, service culture, and memorable visitor experiences.",
  },
  {
    icon: BedDouble,
    title: "Property Quality",
    description:
      "Cleanliness, maintenance, facilities, guest rooms, design, architecture, presentation, and overall quality standards.",
  },
  {
    icon: Landmark,
    title: "Heritage & Local Community",
    description:
      "Cultural authenticity, heritage preservation, local engagement, community support, local sourcing, and destination identity.",
  },
  {
    icon: ShieldCheck,
    title: "Safety",
    description:
      "Health and hygiene standards, emergency preparedness, security procedures, risk management, and regulatory compliance.",
  },
  {
    icon: Leaf,
    title: "Sustainability",
    description:
      "Environmental responsibility, resource management, waste reduction, sustainable purchasing, community impact, and responsible tourism practices.",
  },
];

const stages = [
  {
    icon: FileText,
    code: "Stage 1",
    title: "Application & Registration",
    summary:
      "Applicants submit their certification application and core business information. The application is reviewed to confirm eligibility and certification scope.",
    items: [
      "Certification Application Form",
      "Business Registration Information",
      "Property Information",
      "Contact Details",
      "Website and Social Media Profiles",
    ],
    payCta: {
      label: "Pay Application Fee Here",
      href: "https://buy.stripe.com/6oU9ASeBU3Sw1dxeSL63K06",
    },
  },
  {
    icon: FolderSearch,
    code: "Stage 2",
    title: "Documentation Review",
    summary:
      "Applicants submit supporting documentation across eight evidence categories, demonstrating compliance with WTTQ.Org standards.",
    groups: [
      {
        heading: "Business & Legal Compliance",
        items: [
          "Business Registration Certificate",
          "Tourism License",
          "Accommodation License",
          "Tax Registration",
          "Insurance Certificate",
          "Fire Safety Certification",
          "Health & Safety Compliance Documentation",
        ],
      },
      {
        heading: "Destination & Digital Presence",
        items: [
          "Website URL",
          "Social Media Profiles",
          "Online Booking Links",
          "Destination Marketing Materials",
          "Digital Presence Screenshots",
        ],
      },
      {
        heading: "Hospitality & Guest Experience",
        items: [
          "Guest Service Standards Manual",
          "Customer Service Policy",
          "Complaint Management Procedures",
          "Guest Satisfaction Reports",
          "Staff Training Records",
          "Service Recovery Procedures",
        ],
      },
      {
        heading: "Property Quality",
        items: [
          "Housekeeping Standards",
          "Maintenance Procedures",
          "Preventive Maintenance Schedules",
          "Inspection Records",
          "Property Photography",
        ],
      },
      {
        heading: "Food & Beverage (where applicable)",
        items: [
          "Food Safety Certification",
          "HACCP Documentation",
          "Kitchen Inspection Reports",
          "Menus",
          "Supplier Information",
          "Culinary Awards or Recognition",
        ],
      },
      {
        heading: "Heritage & Local Community",
        items: [
          "Community Engagement Programs",
          "Local Supplier Policies",
          "Heritage Preservation Initiatives",
          "Cultural Experience Programs",
          "Local Partnership Documentation",
        ],
      },
      {
        heading: "Safety",
        items: [
          "Health & Safety Policy",
          "Emergency Response Plan",
          "Crisis Management Procedures",
          "Security Procedures",
          "Risk Assessment Reports",
          "First Aid Procedures",
        ],
      },
      {
        heading: "Sustainability",
        items: [
          "Sustainability Policy",
          "Environmental Management Plan",
          "Energy Management Program",
          "Water Conservation Measures",
          "Waste Management Procedures",
          "Sustainability Performance Reports",
        ],
      },
    ],
  },
  {
    icon: ClipboardList,
    code: "Stage 3",
    title: "Excellence Questionnaire",
    summary:
      "Applicants complete the WTTQ.Org Global Tourism & Hospitality Excellence Certification Questionnaire — 135 evaluation criteria scored on a standardized 0–5 scale.",
    items: [
      "Destination",
      "Hospitality",
      "Property Quality",
      "Heritage & Local Community",
      "Safety",
      "Sustainability",
    ],
    cta: {
      label: "Open the Property Questionnaire",
      href: "/questionnaire",
      restricted: true,
    },
  },
  {
    icon: SearchCheck,
    code: "Stage 4",
    title: "Evidence Verification",
    summary:
      "WTTQ.Org reviewers verify the submitted information. Additional information may be requested during the review process.",
    items: [
      "Documentation review",
      "Website review",
      "Social media review",
      "Guest review analysis",
      "Supporting evidence verification",
      "Interviews where required",
    ],
    priceCta: {
      label: "Certification Fee — Price Upon Request",
      href: "/#apply",
    },
  },
  {
    icon: Gavel,
    code: "Stage 5",
    title: "Independent Audit",
    summary:
      "Depending on certification level and property type, WTTQ.Org may conduct one or more audit formats. Gold candidates may face additional verification.",
    groups: [
      {
        heading: "Remote Audit",
        items: [
          "Verification through virtual review and documentation assessment.",
        ],
      },
      {
        heading: "Virtual Inspection",
        items: ["Video-based property inspection and management interview."],
      },
      {
        heading: "On-Site Audit",
        items: [
          "Physical inspection conducted by an approved WTTQ.Org assessor.",
          "Mystery Guest assessment — an incognito visit by a trained WTTQ.Org evaluator who experiences the property as a regular guest to verify real-world service quality, hospitality, and operational consistency.",
        ],
      },
    ],
  },
];

const scoringScale = [
  { score: "0", rating: "Not Available / Non-Compliant" },
  { score: "1", rating: "Poor" },
  { score: "2", rating: "Basic" },
  { score: "3", rating: "Good" },
  { score: "4", rating: "Very Good" },
  { score: "5", rating: "Excellent / Best Practice" },
];

const tiers = [
  {
    name: "Bronze",
    label: "Certified Quality",
    range: "70 – 79",
    badge: BADGE_URLS.bronze,
    accent: "#B87333",
    description:
      "Recognizes organizations that consistently meet established quality standards and provide a reliable, safe, and professional visitor experience.",
  },
  {
    name: "Silver",
    label: "Certified Excellence",
    range: "80 – 89",
    badge: BADGE_URLS.silver,
    accent: "#9CA3AF",
    description:
      "Recognizes organizations that exceed standard expectations and demonstrate excellence across service delivery, guest satisfaction, hospitality, and operational performance.",
  },
  {
    name: "Gold",
    label: "Certified World-Class Excellence",
    range: "90 – 100",
    badge: BADGE_URLS.gold,
    accent: "#D4AF37",
    description:
      "Recognizes organizations that achieve exceptional performance across all certification pillars and serve as benchmarks of excellence within the tourism and hospitality industry. Additional Gold requirements may apply.",
  },
];

const goldRequirements = [
  "Excellence Portfolio",
  "Management Presentation",
  "Community Impact Evidence",
  "Sustainability Performance Evidence",
  "Awards and Recognition",
  "Professional Photography",
  "Additional Interviews",
  "Independent Verification",
];

const validityPoints = [
  "Periodic review",
  "Reassessment",
  "Ongoing compliance monitoring",
  "Renewal audits",
];

const MethodologyPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const canAccessQuestionnaire = user?.role === "travel_partner";

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [location.hash, location.pathname]);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const pdfUrl = `${BACKEND_URL}/api/methodology/pdf`;

  return (
    <div className="min-h-screen bg-white" data-testid="methodology-page">
      <Header />

      {/* Hero */}
      <section
        className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-[#0B132B] text-white overflow-hidden"
        data-testid="methodology-hero"
      >
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-[#D4AF37] rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-[28rem] h-[28rem] bg-[#D4AF37] rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-6">
              Certification Methodology
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              A transparent, independent,
              <br />
              <span className="text-[#D4AF37]">evidence-based</span> assessment.
            </h1>
            <p className="text-white/80 text-lg leading-relaxed mb-8">
              The World Tourism & Travel Quality Organization (WTTQ.Org) applies
              a comprehensive certification methodology designed to evaluate
              quality, hospitality excellence, operational standards, guest
              experience, sustainability, and destination contribution across
              the global tourism industry.
            </p>
            <p className="text-white/70 text-base leading-relaxed">
              Our process combines documentation review, quality assessment,
              evidence verification, and independent evaluation to ensure every
              certified organization meets internationally benchmarked
              standards.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0B132B] px-7 py-3.5 text-sm font-semibold tracking-wider uppercase hover:bg-[#E8C456] transition-colors duration-300"
                data-testid="methodology-download-pdf-hero"
              >
                <Download className="w-4 h-4" />
                Download PDF
              </a>
              <a
                href="#methodology-pillars"
                className="inline-flex items-center gap-2 text-white/80 hover:text-[#D4AF37] text-sm font-medium tracking-wide transition-colors border-b border-white/20 hover:border-[#D4AF37] pb-1"
                data-testid="methodology-explore"
              >
                Explore the framework
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Six Core Pillars */}
      <section
        id="methodology-pillars"
        className="py-20 lg:py-28 bg-[#FAFAFA]"
        data-testid="methodology-pillars"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">
              The Framework
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B132B] leading-tight">
              Six core pillars of tourism and hospitality excellence.
            </h2>
            <p className="mt-6 text-base text-[#0B132B]/70 leading-relaxed">
              All applicants are assessed against these foundational pillars —
              each defining a critical dimension of quality, responsibility, and
              guest experience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pillars.map((pillar, idx) => {
              const Icon = pillar.icon;
              return (
                <div
                  key={pillar.title}
                  className="group bg-white border border-[#0B132B]/8 p-8 transition-all duration-300 hover:border-[#D4AF37] hover:shadow-[0_20px_60px_-25px_rgba(212,175,55,0.45)]"
                  data-testid={`pillar-card-${idx + 1}`}
                >
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#0B132B] text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-[#0B132B] transition-colors">
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="text-xs tracking-[0.25em] uppercase text-[#0B132B]/40 font-medium">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-[#0B132B] mb-3">
                    {pillar.title}
                  </h3>
                  <p className="text-sm text-[#0B132B]/70 leading-relaxed">
                    {pillar.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Five Assessment Stages */}
      <section
        className="py-20 lg:py-28 bg-white"
        data-testid="methodology-stages"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">
              Assessment Methodology
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B132B] leading-tight">
              Five stages of independent evaluation.
            </h2>
            <p className="mt-6 text-base text-[#0B132B]/70 leading-relaxed">
              Each stage builds a complete portrait of operational quality —
              from application intake through independent audit.
            </p>
          </div>

          <Accordion
            type="single"
            collapsible
            defaultValue="Stage 1"
            className="space-y-3"
            data-testid="stages-accordion"
          >
            {stages.map((stage) => {
              const Icon = stage.icon;
              return (
                <AccordionItem
                  key={stage.code}
                  value={stage.code}
                  className="border border-[#0B132B]/10 bg-[#FAFAFA] px-6 data-[state=open]:bg-white data-[state=open]:border-[#D4AF37] data-[state=open]:shadow-[0_20px_60px_-30px_rgba(11,19,43,0.35)]"
                  data-testid={`stage-${stage.code.replace(" ", "-").toLowerCase()}`}
                >
                  <AccordionTrigger className="hover:no-underline py-6">
                    <div className="flex items-center gap-5 text-left flex-1">
                      <div className="w-12 h-12 flex items-center justify-center bg-[#0B132B] text-[#D4AF37] shrink-0">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-1">
                          {stage.code}
                        </p>
                        <h3 className="font-heading text-xl sm:text-2xl font-bold text-[#0B132B]">
                          {stage.title}
                        </h3>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pt-2">
                    <p className="text-[#0B132B]/75 leading-relaxed mb-6 max-w-3xl">
                      {stage.summary}
                    </p>

                    {stage.items && (
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                        {stage.items.map((item) => (
                          <li
                            key={item}
                            className="flex items-start gap-2 text-sm text-[#0B132B]/80"
                          >
                            <CheckCircle2 className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {stage.groups && (
                      <div className="space-y-6">
                        {stage.groups.map((group) => (
                          <div key={group.heading}>
                            <h4 className="font-heading text-base font-bold text-[#0B132B] mb-3 pb-2 border-b border-[#0B132B]/10">
                              {group.heading}
                            </h4>
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                              {group.items.map((item) => (
                                <li
                                  key={item}
                                  className="flex items-start gap-2 text-sm text-[#0B132B]/75"
                                >
                                  <span className="w-1 h-1 rounded-full bg-[#D4AF37] mt-2 shrink-0" />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}

                    {stage.payCta && (
                      <a
                        href={stage.payCta.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-6 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0B132B] px-6 py-3 text-sm font-semibold tracking-wider uppercase hover:bg-[#E8C456] transition-colors duration-300"
                        data-testid={`pay-cta-${stage.code.replace(" ", "-").toLowerCase()}`}
                      >
                        <CreditCard className="w-4 h-4" />
                        {stage.payCta.label}
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    )}

                    {stage.priceCta && (
                      <Link
                        to={stage.priceCta.href}
                        className="mt-6 inline-flex items-center gap-2 bg-[#0B132B] text-white px-6 py-3 text-sm font-semibold tracking-wider uppercase hover:bg-[#1a2542] transition-colors duration-300"
                        data-testid={`price-cta-${stage.code.replace(" ", "-").toLowerCase()}`}
                      >
                        <Tag className="w-4 h-4 text-[#D4AF37]" />
                        {stage.priceCta.label}
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    )}

                    {stage.cta && stage.cta.restricted && (
                      canAccessQuestionnaire ? (
                        <Link
                          to={stage.cta.href}
                          className="mt-6 inline-flex items-center gap-2 bg-[#0B132B] text-white px-6 py-3 text-sm font-semibold tracking-wider uppercase hover:bg-[#1a2542] transition-colors duration-300"
                          data-testid="stage3-questionnaire-cta"
                        >
                          <ClipboardCheck className="w-4 h-4" />
                          {stage.cta.label}
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      ) : (
                        <div
                          className="mt-6 inline-flex items-center gap-2 bg-[#FAFAFA] border border-[#0B132B]/15 text-[#0B132B]/60 px-6 py-3 text-sm font-medium tracking-wide"
                          data-testid="stage3-questionnaire-restricted"
                        >
                          <Lock className="w-4 h-4 text-[#D4AF37]" />
                          {user
                            ? "Questionnaire access reserved for Travel Partners"
                            : "Sign in as a Travel Partner to access the questionnaire"}
                          {!user && (
                            <Link
                              to="/signin"
                              className="ml-2 underline text-[#0B132B] hover:text-[#D4AF37]"
                              data-testid="stage3-signin-link"
                            >
                              Sign in
                            </Link>
                          )}
                        </div>
                      )
                    )}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </section>

      {/* Scoring Scale */}
      <section
        className="py-20 lg:py-28 bg-[#0B132B] text-white"
        data-testid="methodology-scoring"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">
                Scoring Scale
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6">
                A standardized scale across 120 criteria.
              </h2>
              <p className="text-white/75 leading-relaxed mb-4">
                Each of the 135 evaluation criteria — spanning all six pillars —
                is rated independently on a 0 to 5 scale, producing a composite
                score that determines certification tier.
              </p>
              <p className="text-white/60 text-sm leading-relaxed">
                Results are aggregated and weighted to produce a final
                certification score, audited and reviewed by WTTQ.Org&apos;s
                independent evaluation panel.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 backdrop-blur-sm">
              <Table data-testid="scoring-scale-table">
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-[#D4AF37] uppercase text-xs tracking-[0.25em] w-24">
                      Score
                    </TableHead>
                    <TableHead className="text-[#D4AF37] uppercase text-xs tracking-[0.25em]">
                      Rating
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scoringScale.map((row) => (
                    <TableRow
                      key={row.score}
                      className="border-white/10 hover:bg-white/5"
                      data-testid={`score-row-${row.score}`}
                    >
                      <TableCell className="font-heading text-2xl font-bold text-[#D4AF37]">
                        {row.score}
                      </TableCell>
                      <TableCell className="text-white/90 text-sm">
                        {row.rating}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </section>

      {/* Certification Levels */}
      <section
        id="certification-levels"
        className="py-20 lg:py-28 bg-[#FAFAFA]"
        data-testid="methodology-tiers"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">
              Certification Levels
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B132B] leading-tight">
              Three tiers. One global standard.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tiers.map((tier) => (
              <div
                key={tier.name}
                className="relative bg-white border border-[#0B132B]/10 p-8 flex flex-col"
                style={{ borderTop: `4px solid ${tier.accent}` }}
                data-testid={`tier-card-${tier.name.toLowerCase()}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <p
                      className="text-xs tracking-[0.25em] uppercase font-semibold mb-2"
                      style={{ color: tier.accent }}
                    >
                      {tier.label}
                    </p>
                    <h3 className="font-heading text-3xl font-bold text-[#0B132B]">
                      {tier.name}
                    </h3>
                  </div>
                  <img
                    src={tier.badge}
                    alt={`${tier.name} certification badge`}
                    className="w-16 h-16 object-contain"
                  />
                </div>

                <div className="mb-5 pb-5 border-b border-[#0B132B]/10">
                  <p className="text-xs uppercase tracking-widest text-[#0B132B]/50 mb-1">
                    Score Range
                  </p>
                  <p className="font-heading text-2xl font-bold text-[#0B132B]">
                    {tier.range}
                  </p>
                </div>

                <p className="text-sm text-[#0B132B]/75 leading-relaxed">
                  {tier.description}
                </p>
              </div>
            ))}
          </div>

          {/* Gold additional requirements */}
          <div
            className="mt-10 bg-white border border-[#D4AF37]/40 p-8 lg:p-10"
            data-testid="gold-additional-requirements"
          >
            <div className="flex flex-col lg:flex-row lg:items-start gap-8">
              <div className="lg:w-1/3">
                <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-3">
                  Gold Certification
                </p>
                <h3 className="font-heading text-2xl sm:text-3xl font-bold text-[#0B132B] mb-3">
                  Additional verification requirements.
                </h3>
                <p className="text-sm text-[#0B132B]/70 leading-relaxed">
                  Properties seeking Gold Certification must demonstrate
                  exceptional performance across all assessment categories.
                  Gold is reserved for organizations demonstrating world-class
                  tourism and hospitality excellence.
                </p>
              </div>
              <div className="lg:w-2/3">
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {goldRequirements.map((req) => (
                    <li
                      key={req}
                      className="flex items-start gap-3 text-sm text-[#0B132B]/85"
                    >
                      <CheckCircle2 className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Validity + Commitment */}
      <section className="py-20 lg:py-28 bg-white" data-testid="methodology-validity">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">
                Certification Validity
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B132B] leading-tight mb-6">
                Maintained, monitored, and renewed.
              </h2>
              <p className="text-[#0B132B]/75 leading-relaxed mb-6">
                WTTQ.Org Certification is valid for a defined certification
                period and may be subject to:
              </p>
              <ul className="space-y-3">
                {validityPoints.map((point) => (
                  <li
                    key={point}
                    className="flex items-start gap-3 text-[#0B132B]/85"
                  >
                    <CheckCircle2 className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm text-[#0B132B]/60 italic">
                Certified organizations are expected to maintain standards
                throughout the certification period.
              </p>
            </div>

            <div className="bg-[#0B132B] text-white p-10 lg:p-12 relative overflow-hidden">
              <div className="absolute -top-12 -right-12 w-48 h-48 bg-[#D4AF37]/10 rounded-full blur-3xl pointer-events-none" />
              <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4 relative">
                Our Commitment
              </p>
              <h3 className="font-heading text-3xl font-bold leading-tight mb-5 relative">
                Quality, excellence, and responsible tourism.
              </h3>
              <p className="text-white/80 leading-relaxed mb-4 relative">
                WTTQ.Org is committed to maintaining a transparent, independent,
                and evidence-based certification process that promotes quality,
                excellence, sustainability, cultural authenticity, and
                responsible tourism development worldwide.
              </p>
              <p className="text-white/70 text-sm leading-relaxed relative">
                Our methodology gives travelers, tourism professionals, tourism
                boards, and industry stakeholders confidence that certified
                organizations have been assessed against internationally
                benchmarked standards.
              </p>
              <div className="mt-8 pt-6 border-t border-white/10 relative">
                <p className="font-heading text-xl font-bold text-white">
                  WTTQ.Org
                </p>
                <p className="text-[#D4AF37] text-sm tracking-wide">
                  Advancing Quality, Excellence, and Responsible Tourism Worldwide.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#FAFAFA] border-t border-[#0B132B]/5" data-testid="methodology-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B132B] mb-5">
            Ready to begin your certification journey?
          </h2>
          <p className="text-[#0B132B]/70 mb-8 max-w-2xl mx-auto leading-relaxed">
            Apply now to start the WTTQ.Org assessment process. Our independent
            evaluators will guide you through every stage.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/#apply"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0B132B] px-8 py-4 text-sm font-semibold tracking-wider uppercase hover:bg-[#E8C456] transition-colors duration-300"
              data-testid="methodology-apply-cta"
            >
              Apply for Certification
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-[#0B132B] text-[#0B132B] px-8 py-4 text-sm font-semibold tracking-wider uppercase hover:bg-[#0B132B] hover:text-white transition-colors duration-300"
              data-testid="methodology-download-pdf-cta"
            >
              <Download className="w-4 h-4" />
              Download Methodology PDF
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MethodologyPage;
